<template>
	<div :class="surface === 'sheet' ? 'px-2' : ''">
		<!-- Provider Tabs -->
		<div v-if="providers.length > 1" class="flex gap-1 border-b pb-2 mb-4 overflow-x-auto">
			<button
				v-for="(provider, index) in providers"
				:key="index"
				@click="activeProvider = index; metaGroupingTab = 0"
				class="px-3 py-1.5 text-xs font-medium uppercase transition-colors whitespace-nowrap rounded-md"
				:class="activeProvider === index ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'"
			>
				{{ provider.provider }}
			</button>
		</div>

		<div class="flex flex-col md:flex-row gap-4">
			<!-- Grouping Sidebar -->
			<aside v-if="providerGroups.length > 1" class="md:w-1/4 shrink-0 space-y-0.5">
				<button
					v-for="(group, gIndex) in providerGroups"
					:key="gIndex"
					@click="metaGroupingTab = gIndex"
					class="w-full text-left px-3 py-2 text-sm font-medium transition-colors rounded-md"
					:class="metaGroupingTab === gIndex ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50'"
				>
					{{ group.groupName || 'General' }}
				</button>
			</aside>

			<!-- Fields -->
			<section class="flex-1">
				<!-- Filter + view toggles -->
				<div class="flex items-center gap-2 mb-4">
					<div v-if="activeGroupItems.length > 10" class="relative flex-1">
						<Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
						<Input v-model="filterText" placeholder="Filter fields..." class="pl-8 h-8 text-sm" />
					</div>
					<div class="flex items-center gap-1 shrink-0">
						<!-- Show All / Favourites toggle -->
						<button
							@click="showFavouritesOnly = !showFavouritesOnly"
							class="flex items-center gap-1 text-xs transition-colors px-2 py-1 rounded border"
							:class="showFavouritesOnly ? 'bg-amber-50 text-amber-600 border-amber-200' : 'text-muted-foreground hover:text-foreground border-input'"
						>
							<Star class="h-3.5 w-3.5" :class="showFavouritesOnly ? 'fill-amber-400 text-amber-400' : 'text-gray-400'" />
							<span>{{ showFavouritesOnly ? 'Favourites' : 'All' }}</span>
						</button>
						<!-- Derived toggle -->
						<button
							v-if="hasDerivedFields"
							@click="toggleDerived()"
							class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded border"
							:class="hideDerived ? 'border-transparent' : 'bg-muted border-input'"
						>
							<Eye v-if="!hideDerived" class="h-3.5 w-3.5" />
							<EyeOff v-else class="h-3.5 w-3.5" />
							<span>Derived</span>
						</button>
					</div>
				</div>

				<!-- Responsive grid: small fields 2-up on md, 3-up on lg -->
				<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
				<div v-for="(item, i) in filteredItems" :key="item.key"
					:class="isWideField(item) ? 'md:col-span-2 lg:col-span-3 space-y-1.5' : 'space-y-1.5'">

					<!-- Label + char count -->
					<div class="flex items-center justify-between gap-2">
						<Label class="text-xs text-muted-foreground">{{ item.label }}</Label>
						<div class="flex items-center gap-1.5">
							<span v-if="item.maxsize && item.maxsize > 0 && item.type === 'textfield'" class="text-[10px] text-muted-foreground">
								{{ (getMeta(item.key) || '').length }}/{{ item.maxsize }}
							</span>
							<button @click.prevent="toggleFavourite(item.key)" class="shrink-0 hover:scale-110 transition-transform" :title="isFavourite(item.key) ? 'Remove from favourites' : 'Add to favourites'">
								<Star class="h-3.5 w-3.5" :class="isFavourite(item.key) ? 'fill-amber-400 text-amber-400' : 'text-gray-300 hover:text-amber-400'" />
							</button>
						</div>
					</div>

					<!-- ── Standard field types ── -->

					<!-- Text field -->
					<input
						v-if="item.type === 'textfield' || item.type === 'freeform'"
						:value="getMeta(item.key)"
						:maxlength="item.maxsize || undefined"
						:placeholder="item.prefill || ''"
						class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						@input="setMeta(item.key, ($event.target as HTMLInputElement).value)"
					/>

					<!-- Numeric -->
					<input
						v-else-if="item.type === 'numeric'"
						:value="getMeta(item.key)"
						type="number"
						:placeholder="item.prefill || ''"
						class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						@input="setMeta(item.key, ($event.target as HTMLInputElement).value)"
					/>

					<!-- Textarea -->
					<textarea
						v-else-if="item.type === 'textarea'"
						:value="getMeta(item.key)"
						rows="2"
						class="flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
						:placeholder="item.prefill || ''"
						@input="setMeta(item.key, ($event.target as HTMLTextAreaElement).value)"
					/>

					<!-- Date field -->
					<input
						v-else-if="item.type === 'date' || item.type === 'datefield'"
						:value="getMeta(item.key)"
						type="date"
						class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						@input="setMeta(item.key, ($event.target as HTMLInputElement).value)"
					/>

					<!-- Dropdown -->
					<select
						v-else-if="item.type === 'dropdown'"
						:value="getMeta(item.key)"
						class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						@change="setMeta(item.key, ($event.target as HTMLSelectElement).value)"
					>
						<option value="">Choose Option</option>
						<option v-for="opt in parseOptions(item.options)" :key="opt" :value="opt">
							{{ opt }}
						</option>
					</select>

					<!-- Multiselect -->
					<select
						v-else-if="item.type === 'multiselect'"
						v-model="localMeta[item.key]"
						multiple
						class="flex w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[56px]"
						@change="handleMultiSelect(item.key, $event)"
					>
						<option v-for="opt in parseOptions(item.options)" :key="opt" :value="opt">
							{{ opt }}
						</option>
					</select>

					<!-- Checkbox -->
					<div v-else-if="item.type === 'checkbox'" class="flex items-center gap-2">
						<input
							type="checkbox"
							v-model="localMeta[item.key]"
							class="rounded border-input"
							@change="emitChange(item.key, localMeta[item.key])"
						/>
						<span class="text-sm text-muted-foreground">{{ item.prefill || item.label }}</span>
					</div>

					<!-- Fallback for unknown types -->
					<input
						v-else-if="item.source !== 'api' && !['textfield','freeform','numeric','textarea','date','datefield','dropdown','multiselect','checkbox'].includes(item.type)"
						:value="getMeta(item.key)"
						class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						@input="setMeta(item.key, ($event.target as HTMLInputElement).value)"
					/>

					<!-- ── API / Taxonomy multi-select search ── -->
					<div v-if="item.source === 'api'" class="space-y-2">
						<!-- Selected items as removable pills -->
						<div v-if="apiSelectedLabels[item.key]?.length" class="flex flex-wrap gap-1">
							<span
								v-for="(label, idx) in apiSelectedLabels[item.key]"
								:key="idx"
								class="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full"
							>
								{{ label }}
								<button @click="removeApiSelection(item.key, idx)" class="hover:text-destructive">
									<X class="h-3 w-3" />
								</button>
							</span>
						</div>

						<!-- Current saved value -->
						<div v-if="getMeta(item.key) && !apiSelectedLabels[item.key]?.length" class="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
							{{ getMeta(item.key) }}
						</div>

						<!-- Search input -->
						<div class="relative">
							<Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
							<Input
								v-model="apiSearchQuery[item.key]"
								placeholder="Search keywords (min 4 chars)..."
								class="pl-7 h-8 text-xs"
								@input="debouncedApiSearch(item)"
							/>
						</div>

						<p v-if="apiSearchLoading[item.key]" class="text-[10px] text-muted-foreground">Searching...</p>

						<!-- Tree results with checkboxes -->
						<div v-if="apiSearchTreeResults[item.key]?.length" class="border rounded-md max-h-60 overflow-y-auto">
							<template v-for="(node, ni) in apiSearchTreeResults[item.key]" :key="'root-' + ni">
								<MetaTreeNode
									:node="node"
									:depth="0"
									:field-key="item.key"
									:selected-ids="apiSelectedItems[item.key] || []"
									:expanded-ids="apiExpandedNodes[item.key] || []"
									@toggle-select="toggleApiResult(item.key, $event)"
									@toggle-expand="toggleExpand(item.key, $event)"
								/>
							</template>
						</div>

						<p v-if="!apiSearchLoading[item.key] && apiSearchQuery[item.key]?.length >= 4 && !apiSearchTreeResults[item.key]?.length" class="text-[10px] text-muted-foreground">No results</p>

						<!-- Apply actions — shown when selections exist -->
						<div v-if="apiSelectedLabels[item.key]?.length" class="flex items-center gap-1">
							<Button variant="default" size="sm" class="h-6 px-2 text-xs" @click="applyApiSearch(item, 'apply')">Set</Button>
							<Button v-if="getMeta(item.key)" variant="outline" size="sm" class="h-6 px-2 text-xs" @click="applyApiSearch(item, 'append')">Append</Button>
							<Button v-if="getMeta(item.key)" variant="secondary" size="sm" class="h-6 px-2 text-xs" @click="applyApiSearch(item, 'replace')">Replace</Button>
							<Button variant="ghost" size="sm" class="h-6 px-2 text-xs text-muted-foreground" @click="clearApiSelection(item.key)">Clear</Button>
						</div>
					</div>
				</div>
				</div>

				<div v-if="filteredItems.length === 0" class="text-center py-8 text-sm text-muted-foreground">
					{{ filterText ? 'No fields match your filter' : showFavouritesOnly ? 'No favourite fields set — star some fields first' : 'No metadata fields configured' }}
				</div>
			</section>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, inject, watch, nextTick, type Ref } from 'vue'
import {updateUser} from '@/services/User'
import type { PropType } from 'vue'
import type { MetaGroup, FileMeta } from '@/types/Media'
import type { Member } from '@/types/Member'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Search, X, Check, Eye, EyeOff,Star } from 'lucide-vue-next'
import vhostService from '@/services/Vhost'
import MetaTreeNode from '@/components/MetaTreeNode.vue'

interface Provider {
	provider: string
	vhost: number
	id: number
}

interface ApiResult {
	id: string | number
	label: string
	parentid?: string | number
	externalid?: string | number
	children?: ApiResult[]
	hasChildren?: boolean
}

const props = defineProps({
	providers: { type: Array as PropType<Provider[]>, required: true },
	groups: { type: Array as PropType<MetaGroup[]>, required: true },
	metadata: { type: Object as PropType<Record<string, any>>, required: true },
	surface: { type: String as PropType<'sheet' | 'modal' | 'page'>, default: 'page' },
	page: { type: String, default: 'mediaitem' },
	hiddenFields: { type: Array as PropType<string[]>, default: () => [] },
})

const emit = defineEmits(['update:metadata', 'change', 'request-meta-search', 'close'])

const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)
const { search3rdPartyMeta, generateValue } = vhostService()

const activeProvider = ref(0)
const metaGroupingTab = ref<number>(0)
const filterText = ref('')
const hideDerived = ref(true)
const showFavouritesOnly = ref(false)
const favouriteFields = ref<string[]>([])

// ─── Local reactive copy of metadata ───
// We maintain our own reactive object so reads/writes are fully tracked by Vue.
// This avoids the pitfall of mutating props directly (which Vue 3 may not re-render).
const localMeta = reactive<Record<string, any>>({})

// Sync from parent prop → local on mount and whenever parent changes
watch(
	() => props.metadata,
	(newMeta) => {
		if (!newMeta) return
		for (const key of Object.keys(newMeta)) {
			if (localMeta[key] !== newMeta[key]) {
				localMeta[key] = newMeta[key]
			}
		}
	},
	{ deep: true, immediate: true },
)

// Reactive getter — reads from our local reactive state (always triggers re-render)
function getMeta(key: string): string {
	return localMeta[key] ?? ''
}

// Setter — writes to local reactive AND emits to parent
function setMeta(key: string, value: string) {
	localMeta[key] = value
	emitChange(key, value)
}

// Load user preferences from userInfo.meta on init
function loadUserPrefs() {
	const meta = userInfo?.value?.meta
	if (meta) {
		const parsed = typeof meta === 'string' ? (() => { try { return JSON.parse(meta) } catch { return {} } })() : meta
		// Load favourites — default to favourites view if user has any
		const favs = parsed?.metaFavourites?.[props.page]
		if (Array.isArray(favs) && favs.length > 0) {
			favouriteFields.value = favs
			showFavouritesOnly.value = true
		}
		// Load derived field visibility preference
		if (parsed?.metaDerivedVisible?.[props.page] === true) {
			hideDerived.value = false
		}
	}
}
loadUserPrefs()

function isFavourite(key: string): boolean {
	return favouriteFields.value.includes(key)
}

async function toggleFavourite(key: string) {
	const idx = favouriteFields.value.indexOf(key)
	if (idx >= 0) {
		favouriteFields.value.splice(idx, 1)
	} else {
		favouriteFields.value.push(key)
	}
	// Persist to user meta
	await saveUserPrefs()
}

async function saveUserPrefs() {
	if (!userInfo?.value) return
	const meta = userInfo.value.meta
	const parsed = typeof meta === 'string' ? (() => { try { return JSON.parse(meta) } catch { return {} } })() : (meta || {})
	// Save favourites
	if (!parsed.metaFavourites) parsed.metaFavourites = {}
	parsed.metaFavourites[props.page] = [...favouriteFields.value]
	// Save derived visibility
	if (!parsed.metaDerivedVisible) parsed.metaDerivedVisible = {}
	parsed.metaDerivedVisible[props.page] = !hideDerived.value
	try {
		await updateUser({ meta: parsed })
		if (userInfo.value) userInfo.value.meta = parsed
	} catch (err) {
		console.error('Failed to save user prefs:', err)
	}
}

async function toggleDerived() {
	hideDerived.value = !hideDerived.value
	await saveUserPrefs()
}

// API search state
const apiSearchQuery = reactive<Record<string, string>>({})
const apiSearchTreeResults = reactive<Record<string, ApiResult[]>>({})
const apiSearchLoading = reactive<Record<string, boolean>>({})
const apiSelectedItems = reactive<Record<string, (string | number)[]>>({})
const apiSelectedLabels = reactive<Record<string, string[]>>({})
const apiSelectedFullData = reactive<Record<string, any[]>>({})
const apiExpandedNodes = reactive<Record<string, (string | number)[]>>({})
const apiParentId = reactive<Record<string, string>>({})

let searchDebounce: ReturnType<typeof setTimeout> | null = null

const providerGroups = computed(() => {
	const provider = props.providers[activeProvider.value]
	if (!provider) return []
	return props.groups.filter((g: MetaGroup) => g.provider === provider.provider)
})

const activeGroupItems = computed(() => {
	const group = providerGroups.value[metaGroupingTab.value]
	return group?.items || []
})

const filteredItems = computed(() => {
	let items = activeGroupItems.value
	// Filter out fields the user has hidden in their preferences
	if (props.hiddenFields.length > 0) {
		items = items.filter((item: FileMeta) => !props.hiddenFields.includes(item.key))
	}
	// Hide derived fields by default — they get auto-populated from API selections
	if (hideDerived.value) {
		items = items.filter((item: FileMeta) => item.source !== 'derived')
	}
	// Show only favourites when toggled
	if (showFavouritesOnly.value && favouriteFields.value.length > 0) {
		items = items.filter((item: FileMeta) => favouriteFields.value.includes(item.key))
	}
	if (!filterText.value) return items
	const q = filterText.value.toLowerCase()
	return items.filter((item: FileMeta) =>
		(item.label || '').toLowerCase().includes(q) ||
		(item.key || '').toLowerCase().includes(q),
	)
})

const hasDerivedFields = computed(() => {
	return activeGroupItems.value.some((item: FileMeta) => item.source === 'derived')
})

/**
 * Fields that need full grid width — only API fields (they have search + results panel)
 */
function isWideField(item: FileMeta): boolean {
	return item.source === 'api'
}

/**
 * Parse options that may come as a JSON string, a raw array, or a comma-separated string
 */
function parseOptions(options: unknown): string[] {
	if (!options) return []
	if (Array.isArray(options)) return options
	if (typeof options === 'string') {
		try {
			const parsed = JSON.parse(options)
			if (Array.isArray(parsed)) return parsed
			return [String(parsed)]
		} catch {
			if (options.includes(',')) {
				return options.split(',').map((s: string) => s.trim()).filter(Boolean)
			}
			return [options]
		}
	}
	return []
}

function handleMultiSelect(key: string, event: Event) {
	const select = event.target as HTMLSelectElement
	const selected = Array.from(select.selectedOptions).map((o) => o.value)
	localMeta[key] = selected
	emitChange(key, selected)
}

function emitChange(key: string, value: unknown) {
	emit('change', { key, value })
}

// ─── API / Taxonomy Multi-Select Search ───

function initApiField(key: string) {
	if (!apiSearchQuery[key]) apiSearchQuery[key] = ''
	if (!apiSelectedItems[key]) apiSelectedItems[key] = []
	if (!apiSelectedLabels[key]) apiSelectedLabels[key] = []
	if (!apiSelectedFullData[key]) apiSelectedFullData[key] = []
	if (!apiExpandedNodes[key]) apiExpandedNodes[key] = []
	if (!apiParentId[key]) apiParentId[key] = ''
}

function debouncedApiSearch(item: FileMeta) {
	initApiField(item.key)
	if (searchDebounce) clearTimeout(searchDebounce)
	const query = apiSearchQuery[item.key]
	if (!query || query.length < 4) {
		apiSearchTreeResults[item.key] = []
		return
	}
	searchDebounce = setTimeout(() => {
		performApiSearch(item, query)
	}, 400)
}

async function performApiSearch(item: FileMeta, query: string) {
	apiSearchLoading[item.key] = true
	try {
		const vid = vhostId?.value ? parseInt(vhostId.value) : 0
		if (!vid) { apiSearchTreeResults[item.key] = []; return }
		// Extract parentIds from settings.defaultCategories (like legacy searchParentId)
		let parentId = ''
		let settings = item.settings
		if (typeof settings === 'string') {
			try { settings = JSON.parse(settings) } catch { settings = {} }
		}
		if (settings?.defaultCategories && Array.isArray(settings.defaultCategories)) {
			// Legacy JS sends array.toString() which is comma-separated
			parentId = settings.defaultCategories.map((c: any) => c.id || c).join(',')
		}
		console.log('[performApiSearch] field:', item.key, 'query:', query, 'parentId:', parentId, 'settings:', settings)
		const results = await search3rdPartyMeta(vid, query, parentId)
		// Normalize: PHP may return children as an object (associative array) not a JS array
		const toArray = (val: any): any[] => {
			if (!val) return []
			if (Array.isArray(val)) return val
			if (typeof val === 'object') return Object.values(val)
			return []
		}
		const normalizeTree = (nodes: any[]): ApiResult[] => {
			return toArray(nodes)
				.filter((n: any) => n && (n.label || n.id))
				.map((n: any) => {
					const childArr = toArray(n.children)
					// Preserve all original fields so generateValue PHP template has full data
					return {
						...n,
						id: n.id ?? n.label,
						label: n.label || String(n.id),
						children: childArr.length > 0 ? normalizeTree(childArr) : undefined,
					}
				})
		}
		apiSearchTreeResults[item.key] = normalizeTree(results)
		// Auto-expand all nodes with children so the full tree is visible
		const collectExpandableIds = (nodes: ApiResult[]): (string | number)[] => {
			const ids: (string | number)[] = []
			for (const n of nodes) {
				if (n.children?.length) {
					ids.push(n.id)
					ids.push(...collectExpandableIds(n.children))
				}
			}
			return ids
		}
		apiExpandedNodes[item.key] = collectExpandableIds(apiSearchTreeResults[item.key] || [])
	} catch {
		apiSearchTreeResults[item.key] = []
	} finally {
		apiSearchLoading[item.key] = false
	}
}

function toggleExpand(key: string, nodeId: string | number) {
	if (!apiExpandedNodes[key]) apiExpandedNodes[key] = []
	const idx = apiExpandedNodes[key].indexOf(nodeId)
	if (idx >= 0) {
		apiExpandedNodes[key].splice(idx, 1)
	} else {
		apiExpandedNodes[key].push(nodeId)
	}
}

function toggleApiResult(key: string, node: any) {
	initApiField(key)
	const idx = apiSelectedItems[key].indexOf(node.id)
	if (idx >= 0) {
		apiSelectedItems[key].splice(idx, 1)
		apiSelectedLabels[key].splice(idx, 1)
		apiSelectedFullData[key].splice(idx, 1)
	} else {
		apiSelectedItems[key].push(node.id)
		apiSelectedLabels[key].push(node.label || node.name || String(node.id))
		// Store full node data (all fields) — PHP Sabre template needs the complete object
		apiSelectedFullData[key].push(node)
	}
}

function removeApiSelection(key: string, idx: number) {
	apiSelectedItems[key].splice(idx, 1)
	apiSelectedLabels[key].splice(idx, 1)
	apiSelectedFullData[key]?.splice(idx, 1)
}

function clearApiSelection(key: string) {
	apiSelectedItems[key] = []
	apiSelectedLabels[key] = []
	apiSelectedFullData[key] = []
}

async function applyApiSearch(item: FileMeta, mode: 'apply' | 'append' | 'replace') {
	const rawData = apiSelectedFullData[item.key] || []
	console.log('[applyApiSearch] item:', item.key, 'id:', item.id, 'mode:', mode, 'rawData:', JSON.parse(JSON.stringify(rawData)))
	if (rawData.length === 0) { console.log('[applyApiSearch] No data, returning'); return }

	// Strip children from data sent to PHP — only send flat node properties
	const fullData = rawData.map((node: any) => {
		const { children, hasChildren, ...rest } = node
		return rest
	})
	console.log('[applyApiSearch] fullData (stripped):', fullData)

	const vid = vhostId?.value ? parseInt(vhostId.value) : 0
	console.log('[applyApiSearch] vid:', vid, 'item.id:', item.id)
	if (!vid || !item.id) { console.log('[applyApiSearch] Missing vid or item.id, returning'); return }

	// Parse settings if it came as a JSON string
	let settings = item.settings
	if (typeof settings === 'string') {
		try { settings = JSON.parse(settings) } catch { settings = {} }
	}
	console.log('[applyApiSearch] settings:', settings)

	const delim = (mode === 'append') ? (settings?.delim || ';') : ''

	// Helper to set a field value via local reactive state + emit to parent
	const setFieldValue = (key: string, val: string) => {
		const finalVal = (mode === 'append' && localMeta[key])
			? localMeta[key] + delim + val
			: val
		localMeta[key] = finalVal
		emitChange(key, finalVal)
		console.log('[applyApiSearch] Set localMeta[' + key + '] =', finalVal)
	}

	// Call generateValue for the API field itself
	const metaId = parseInt(String(item.id), 10)
	console.log('[applyApiSearch] Calling generateValue for API field, metaID:', metaId)
	const result = await generateValue(vid, metaId, fullData)
	console.log('[applyApiSearch] generateValue result:', result)
	if (result && result.key && result.value != null) {
		setFieldValue(result.key, String(result.value).replaceAll('\\n', '\n'))
	} else {
		console.log('[applyApiSearch] No valid result from generateValue')
	}

	// Call generateValue for the calculated/derived field if configured
	if (settings?.calculatedField) {
		const calcFieldId = parseInt(String(settings.calculatedField), 10)
		console.log('[applyApiSearch] Calling generateValue for calculatedField:', calcFieldId)
		const calcResult = await generateValue(vid, calcFieldId, fullData)
		console.log('[applyApiSearch] calculatedField result:', calcResult)
		if (calcResult && calcResult.key && calcResult.value != null) {
			setFieldValue(calcResult.key, String(calcResult.value).replaceAll('\\n', '\n'))
		}
	} else {
		console.log('[applyApiSearch] No calculatedField in settings')
	}

	// Clear selections after apply
	clearApiSelection(item.key)

	// Reveal derived fields so the user can see what was populated
	if (hideDerived.value) {
		hideDerived.value = false
	}
}
</script>
